import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  ErrorText,
  Field,
  Input,
  Modal,
  Select,
  type Localized,
} from '@/components/ui';

interface Role {
  id: string;
  code: string;
  name: Localized;
  description: string | null;
  isSystem: boolean;
  adminsCount: number;
  permissions: string[];
}

interface Admin {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  role: { id: string; code: string; name: Localized };
}

interface PermissionGroup {
  group: string;
  permissions: Array<{ code: string; description: string | null }>;
}

const GROUP_TITLES: Record<string, string> = {
  accessory: 'Аксессуары',
  admin: 'Сотрудники',
  analytics: 'Аналитика',
  audit: 'Журнал',
  banner: 'Баннеры',
  category: 'Категории',
  customer: 'Клиенты',
  delivery: 'Доставка',
  dictionary: 'Справочники',
  fine: 'Штрафы',
  inventory: 'Склад',
  notification: 'Уведомления',
  offer: 'Оферта',
  order: 'Заказы',
  payment: 'Платежи',
  product: 'Товары',
  promo: 'Промокоды',
  return: 'Возвраты',
  role: 'Роли',
  settings: 'Настройки',
  variant: 'Вариации',
};

export default function StaffPage() {
  const { admin: me } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<(Partial<Admin> & { password?: string }) | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Admin | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const admins = useQuery({
    queryKey: ['admins'],
    queryFn: async () => (await api.get<Admin[]>('/admin/admins')).data,
  });

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get<Role[]>('/admin/roles')).data,
  });

  const catalog = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: async () => (await api.get<PermissionGroup[]>('/admin/roles/permissions')).data,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admins'] });
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
  };
  const fail = (e: unknown) => setError(errorMessage(e));

  const save = useMutation({
    mutationFn: async (value: Partial<Admin> & { password?: string }) => {
      if (value.id) {
        return (
          await api.patch<Admin>(`/admin/admins/${value.id}`, {
            fullName: value.fullName,
            phone: value.phone,
            roleId: value.role?.id,
            isActive: value.isActive,
          })
        ).data;
      }

      const { data } = await api.post<Admin & { password: string }>('/admin/admins', {
        email: value.email,
        fullName: value.fullName,
        phone: value.phone || undefined,
        roleId: value.role?.id,
        password: value.password || undefined,
      });

      setIssued({ email: data.email, password: data.password });
      return data;
    },
    onSuccess: () => {
      setDraft(null);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const toggleActive = useMutation({
    mutationFn: (item: Admin) =>
      api.patch(`/admin/admins/${item.id}`, { isActive: !item.isActive }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const unlock = useMutation({
    mutationFn: (item: Admin) => api.patch(`/admin/admins/${item.id}`, { unlock: true }),
    onSuccess: refresh,
    onError: fail,
  });

  const resetPassword = useMutation({
    mutationFn: async (item: Admin) => {
      const { data } = await api.post<{ password: string }>(
        `/admin/admins/${item.id}/reset-password`,
      );
      setIssued({ email: item.email, password: data.password });
    },
    onSuccess: refresh,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (item: Admin) => api.delete(`/admin/admins/${item.id}`),
    onSuccess: () => {
      setConfirmDelete(null);
      setError(null);
      refresh();
    },
    onError: (e) => {
      setConfirmDelete(null);
      fail(e);
    },
  });

  const saveRole = useMutation({
    mutationFn: (role: Role) =>
      api.patch(`/admin/roles/${role.id}`, { permissions: role.permissions }),
    onSuccess: () => {
      setEditingRole(null);
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const roleName = (role: { code: string; name: Localized }) => role.name.ru ?? role.code;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Сотрудники и роли</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Доступ к панели выдаётся ролью — набором прав, а не отдельными галочками у человека
          </p>
        </div>
        <Button
          onClick={() =>
            setDraft({ isActive: true, role: roles.data?.find((r) => r.code === 'MANAGER') })
          }
        >
          Добавить сотрудника
        </Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card title="Сотрудники">
        {admins.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Сотрудник</th>
                  <th className="pb-2 font-medium">Роль</th>
                  <th className="pb-2 font-medium">Последний вход</th>
                  <th className="pb-2 font-medium">Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(admins.data ?? []).map((item) => {
                  const locked = item.lockedUntil && new Date(item.lockedUntil) > new Date();

                  return (
                    <tr
                      key={item.id}
                      className="border-t border-neutral-100 dark:border-neutral-800"
                    >
                      <td className="py-2">
                        <span className="block">{item.fullName}</span>
                        <span className="block text-xs text-neutral-400">
                          {item.email}
                          {item.id === me?.id && ' · это вы'}
                        </span>
                      </td>
                      <td className="py-2">{roleName(item.role)}</td>
                      <td className="py-2 text-xs text-neutral-500">
                        {item.lastLoginAt
                          ? new Date(item.lastLoginAt).toLocaleString('ru-RU', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'ни разу'}
                      </td>
                      <td className="space-x-1 py-2">
                        {item.isActive ? (
                          <Badge tone="green">активен</Badge>
                        ) : (
                          <Badge tone="red">отключён</Badge>
                        )}
                        {locked && <Badge tone="amber">заблокирован</Badge>}
                        {item.mustChangePassword && <Badge>сменит пароль</Badge>}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" onClick={() => setDraft(item)}>
                          Изм.
                        </Button>
                        {locked && (
                          <Button variant="ghost" onClick={() => unlock.mutate(item)}>
                            Разблок.
                          </Button>
                        )}
                        <Button variant="ghost" onClick={() => resetPassword.mutate(item)}>
                          Пароль
                        </Button>
                        <Button variant="ghost" onClick={() => toggleActive.mutate(item)}>
                          {item.isActive ? 'Отключить' : 'Включить'}
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setConfirmDelete(item)}
                        >
                          Удал.
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Роли">
        <div className="space-y-2">
          {(roles.data ?? []).map((role) => (
            <div
              key={role.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {roleName(role)}{' '}
                  <span className="font-mono text-xs text-neutral-400">{role.code}</span>
                  {role.isSystem && <Badge>системная</Badge>}
                </p>
                <p className="text-xs text-neutral-500">
                  прав {role.permissions.length} · сотрудников {role.adminsCount}
                </p>
              </div>

              <Button
                variant="ghost"
                onClick={() => setEditingRole({ ...role, permissions: [...role.permissions] })}
              >
                {role.code === 'SUPER_ADMIN' ? 'Смотреть' : 'Права'}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* ─── Карточка сотрудника ─── */}
      <Modal
        open={Boolean(draft)}
        title={draft?.id ? 'Изменить сотрудника' : 'Новый сотрудник'}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <div className="space-y-4">
            <Field label="Имя и фамилия">
              <Input
                value={draft.fullName ?? ''}
                onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
              />
            </Field>

            {!draft.id && (
              <Field label="Email" hint="По нему сотрудник входит в панель">
                <Input
                  type="email"
                  value={draft.email ?? ''}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
              </Field>
            )}

            <Field label="Телефон">
              <Input
                value={draft.phone ?? ''}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>

            <Field label="Роль">
              <Select
                value={draft.role?.id ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    role: roles.data?.find((r) => r.id === e.target.value),
                  })
                }
              >
                {(roles.data ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {roleName(role)} · прав {role.permissions.length}
                  </option>
                ))}
              </Select>
            </Field>

            {!draft.id && (
              <Field label="Пароль" hint="Оставьте пустым — сгенерируем и покажем один раз">
                <Input
                  value={draft.password ?? ''}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                />
              </Field>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>
                Отмена
              </Button>
              <Button
                disabled={save.isPending || !draft.fullName || (!draft.id && !draft.email)}
                onClick={() => save.mutate(draft)}
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Выданный пароль ─── */}
      <Modal open={Boolean(issued)} title="Пароль выдан" onClose={() => setIssued(null)}>
        {issued && (
          <div className="space-y-4">
            <p className="text-sm text-neutral-500">
              Передайте его сотруднику — при первом входе панель потребует сменить пароль. Больше
              этот пароль показан не будет.
            </p>
            <div className="rounded-lg bg-neutral-100 p-4 text-center dark:bg-neutral-800">
              <p className="text-xs text-neutral-500">{issued.email}</p>
              <p className="mt-1 font-mono text-lg font-semibold select-all">{issued.password}</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setIssued(null)}>Понятно</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Права роли ─── */}
      <Modal
        open={Boolean(editingRole)}
        title={editingRole ? `Права роли «${roleName(editingRole)}»` : ''}
        onClose={() => setEditingRole(null)}
      >
        {editingRole && (
          <div className="space-y-4">
            {editingRole.code === 'SUPER_ADMIN' && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                У суперадминистратора всегда полный доступ — иначе панель может остаться без учётки,
                способной раздать права обратно.
              </p>
            )}

            <div className="max-h-96 space-y-3 overflow-auto pr-1">
              {(catalog.data ?? []).map((group) => (
                <div key={group.group}>
                  <p className="mb-1 text-xs font-semibold uppercase text-neutral-400">
                    {GROUP_TITLES[group.group] ?? group.group}
                  </p>
                  <div className="space-y-1">
                    {group.permissions.map((permission) => (
                      <Checkbox
                        key={permission.code}
                        checked={editingRole.permissions.includes(permission.code)}
                        disabled={editingRole.code === 'SUPER_ADMIN'}
                        onChange={(e) =>
                          setEditingRole({
                            ...editingRole,
                            permissions: e.target.checked
                              ? [...editingRole.permissions, permission.code]
                              : editingRole.permissions.filter((c) => c !== permission.code),
                          })
                        }
                        label={
                          <span className="font-mono text-xs text-neutral-500">
                            {permission.code}
                          </span>
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditingRole(null)}>
                Закрыть
              </Button>
              {editingRole.code !== 'SUPER_ADMIN' && (
                <Button disabled={saveRole.isPending} onClick={() => saveRole.mutate(editingRole)}>
                  Сохранить права
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Удалить сотрудника?"
        description={
          confirmDelete
            ? `${confirmDelete.fullName} потеряет доступ к панели. Записи в журнале аудита о его действиях останутся.`
            : undefined
        }
        confirmLabel="Удалить"
        tone="danger"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
      />
    </div>
  );
}
